import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import moment from "moment";
import { useAuth } from "../../Common/Configurations/AuthContext";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";

const MeetingModal = ({ show, onHide, leadId, onCommentAdded }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const getComments = async (currentLeadId) => {
    if (!currentLeadId) return;
    setIsLoading(true);
    try {
      const response = await apiService.getById(
        `${config.GETMEETINGBYLEAD}`,
        `${currentLeadId}`
      );
      console.log(response);
      setComments(response.data);
    } catch (err) {
      console.error("Error fetching comments:", err);
      setComments([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (show && leadId) {
      getComments(leadId);
    } else {
      setComments([]);
      setNewComment("");
      setError("");
    }
  }, [show, leadId]);

  const handleInputChange = (e) => {
    setNewComment(e.target.value);
    if (error) {
      setError("");
    }
  };

  const sendCommentToServer = async (payload) => {
    try {
      await apiService.create(config.CREATEMEETING, payload);
      setNewComment("");
      await getComments(leadId);
      if (onCommentAdded) {
        onCommentAdded();
      }
    } catch (err) {
      console.error("Failed to submit comment:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (newComment.trim() === "") {
      setError("Comment cannot be empty.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    const basePayload = {
      meetingId: "00000000-0000-0000-0000-000000000000",
      leadId: leadId,
      userId: user?.id,
      comment: newComment,
      meetingTime: new Date().toISOString(),
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        // Success Callback
        (position) => {
          const { latitude, longitude } = position.coords;
          const finalPayload = {
            ...basePayload,
            fromLatitude: latitude,
            fromLongitude: longitude,
            toLatitude: latitude,
            toLongitude: longitude,
          };
          sendCommentToServer(finalPayload);
        },
        // Error Callback
        (geoError) => {
          console.warn(
            "Geolocation error:",
            geoError.message,
            ". Submitting with 0.0 for location."
          );
          const finalPayload = {
            ...basePayload,
            fromLatitude: 0.0,
            fromLongitude: 0.0,
            toLatitude: 0.0,
            toLongitude: 0.0,
          };
          sendCommentToServer(finalPayload);
        }
      );
    } else {
      console.warn(
        "Geolocation not supported. Submitting with 0.0 for location."
      );
      const finalPayload = {
        ...basePayload,
        fromLatitude: 0.0,
        fromLongitude: 0.0,
        toLatitude: 0.0,
        toLongitude: 0.0,
      };
      sendCommentToServer(finalPayload);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="md" centered>
      <Modal.Header className="bg-primary p-2 text-white" closeButton>
        <Modal.Title className="text-white">
          <i className="ri-chat-3-line me-2"></i>
          Lead Discussion
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: "0" }}>
        {/* Comments History Section */}
        <div
          className="p-3"
          style={{
            backgroundColor: "#f8f9fe",
            borderBottom: "1px solid #e9ecef",
            maxHeight: "300px",
            overflowY: "auto",
          }}
        >
          <h6 className="mb-3 text-primary">
            <i className="ri-history-line me-2"></i>Comments
          </h6>
          {isLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : comments.length > 0 ? (
            comments.map((comment) => (
              <div className="mb-3" key={comment.meetingId}>
                <div className="card shadow-sm">
                  <div className="card-body p-2">
                    <p className="mb-1 text-dark">{comment.comment}</p>

                    {/* ✅ Show user name */}
                    <small className="text-muted d-block">
                      <i className="ri-user-line me-1"></i>
                      {comment.userName || "Unknown User"}
                    </small>

                    <small className="text-muted d-block text-end">
                      {moment
                        .utc(comment.meetingTime)
                        .local()
                        .format("DD MMM YYYY, hh:mm A")}
                    </small>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-4">
              <i className="ri-chat-off-line fs-3 text-muted"></i>
              <p className="text-muted mt-2">
                No comments found. Start the conversation!
              </p>
            </div>
          )}
        </div>

        {/* Add Comment Form Section */}
        <div className="p-3 bg-white">
          <h6 className="mb-2 text-primary">
            <i className="ri-edit-2-line me-2"></i>Add a New Comment
          </h6>
          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-3">
              <textarea
                name="comment"
                value={newComment}
                onChange={handleInputChange}
                className={`form-control ${error ? "is-invalid" : ""}`}
                style={{ minHeight: "80px", resize: "vertical" }}
                placeholder="Write your comment here..."
                disabled={isSubmitting}
                rows="3"
                maxLength="300"
              />
              {error && <div className="invalid-feedback">{error}</div>}
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-danger" onClick={onHide}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1"></span>{" "}
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-2-line me-1"></i> Submit
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default MeetingModal;
